/**
 * React Query hooks for Tenants API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  tenantsService,
  type ListUsersResponse,
} from '@/api/services/tenants.service';

/**
 * Query key factory for tenants-related queries
 */
export const tenantsKeys = {
  all: ['tenants'] as const,
  listAll: () => [...tenantsKeys.all, 'list-all'] as const,
  users: (selectionId?: string) =>
    [...tenantsKeys.all, 'users', selectionId] as const,
};

/**
 * Hook to fetch all available tenants
 * Uses React Query for caching and deduplication
 * This prevents multiple API calls on component re-renders
 */
export const useListAllTenants = () => {
  return useQuery({
    queryKey: tenantsKeys.listAll(),
    queryFn: () => tenantsService.listAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache (formerly cacheTime)
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on mount if data exists in cache
  });
};

/**
 * Hook to fetch all users for current tenant with bulk select support
 */
export const useUsers = (selectionId?: string) => {
  return useQuery<ListUsersResponse, Error>({
    queryKey: tenantsKeys.users(selectionId),
    queryFn: () => tenantsService.listUsers(selectionId),
    staleTime: 30 * 1000, // 30 seconds (shorter for bulk select)
  });
};

/**
 * Hook to select users
 */
export const useSelectUsers = (selectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string[]) =>
      tenantsService.selectUsers(selectionId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantsKeys.users(selectionId),
      });
    },
  });
};

/**
 * Hook to deselect users
 */
export const useDeselectUsers = (selectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string[]) =>
      tenantsService.deselectUsers(selectionId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantsKeys.users(selectionId),
      });
    },
  });
};
